import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * App-level designed replacement for the native window.confirm / window.prompt
 * system dialogs. Mounted once at the app root (see main.tsx) and consumed via
 * the {@link useConfirm} / {@link usePrompt} hooks, which return promises so call
 * sites read almost like the native calls they replace:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: t("…"), danger: true }))) return;
 *
 * Errors keep using the toast system — this is only for blocking yes/no and
 * single-value prompts.
 */

export type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions (delete / revoke / remove). */
  danger?: boolean;
};

export type PromptOptions = ConfirmOptions & {
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  /** Disable confirm until the field is non-empty. */
  required?: boolean;
};

type Req =
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void };

type DialogContextValue = {
  confirm: (input: string | ConfirmOptions) => Promise<boolean>;
  prompt: (input: string | PromptOptions) => Promise<string | null>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

function normalize<T extends ConfirmOptions>(input: string | T): T {
  return (typeof input === "string" ? { message: input } : input) as T;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // FIFO queue: overlapping requests (e.g. two rapid clicks) each get their own
  // promise and are shown one after another — no resolver is ever overwritten.
  const queueRef = useRef<Req[]>([]);
  const [current, setCurrent] = useState<Req | null>(null);
  const [value, setValue] = useState("");
  const valueRef = useRef(""); // mirrors `value` so the keydown handler need not depend on it
  const prevFocusRef = useRef<HTMLElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const showHead = useCallback(() => {
    const head = queueRef.current[0] ?? null;
    const initial = head?.kind === "prompt" ? (head.opts.defaultValue ?? "") : "";
    valueRef.current = initial;
    setValue(initial);
    setCurrent(head);
  }, []);

  const enqueue = useCallback((req: Req) => {
    queueRef.current.push(req);
    if (queueRef.current.length === 1) {
      prevFocusRef.current = document.activeElement as HTMLElement | null;
      showHead();
    }
  }, [showHead]);

  const confirm = useCallback(
    (input: string | ConfirmOptions) =>
      new Promise<boolean>((resolve) => enqueue({ kind: "confirm", opts: normalize(input), resolve })),
    [enqueue],
  );

  const prompt = useCallback(
    (input: string | PromptOptions) =>
      new Promise<string | null>((resolve) =>
        enqueue({ kind: "prompt", opts: normalize<PromptOptions>(input), resolve }),
      ),
    [enqueue],
  );

  const settle = useCallback((result: boolean | string | null) => {
    const req = queueRef.current.shift();
    if (req?.kind === "confirm") req.resolve(result === true);
    else if (req?.kind === "prompt") req.resolve(typeof result === "string" ? result : null);
    showHead(); // shows the next queued request, or clears to null
  }, [showHead]);

  const ctx = useMemo<DialogContextValue>(() => ({ confirm, prompt }), [confirm, prompt]);

  const promptOpts = current?.kind === "prompt" ? current.opts : null;
  const canConfirm = !promptOpts?.required || value.trim().length > 0;

  // Cancel resolves to `false` (confirm) or `null` (prompt); accept returns the value.
  const cancel = useCallback(() => settle(current?.kind === "prompt" ? null : false), [settle, current]);
  const accept = useCallback(
    () => settle(current?.kind === "prompt" ? valueRef.current : true),
    [settle, current],
  );

  // Focus the primary control + wire Escape/Enter. Depends on `current` only (not
  // `value`) — valueRef keeps the handler current without re-binding on each keystroke.
  useEffect(() => {
    if (!current) return;
    const el = current.kind === "prompt" ? inputRef.current : confirmBtnRef.current;
    el?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (e.key === "Enter" && !(current.kind === "prompt" && promptOpts?.multiline)) {
        if (current.kind === "prompt" && promptOpts?.required && valueRef.current.trim().length === 0) return;
        e.preventDefault();
        accept();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, cancel, accept, promptOpts]);

  // Restore focus to the trigger once the whole queue has drained.
  useEffect(() => {
    if (current) return;
    const el = prevFocusRef.current;
    prevFocusRef.current = null;
    el?.focus?.();
  }, [current]);

  const ariaLabel =
    current?.opts.title ??
    (typeof current?.opts.message === "string" ? current.opts.message : current?.opts.confirmLabel) ??
    t("dialog.confirm", "Confirm");

  return (
    <DialogContext.Provider value={ctx}>
      {children}
      {current && (
        // The overlay is a decorative click-catcher (dismissal is also on Escape
        // and the Cancel button), so it stays out of the a11y tree; the dialog
        // semantics live on the panel itself.
        <div role="presentation" style={overlay} onClick={(e) => { if (e.target === e.currentTarget) cancel(); }}>
          <div role="dialog" aria-modal="true" aria-label={ariaLabel} style={panel}>
            {current.opts.title && <h3 style={titleStyle}>{current.opts.title}</h3>}
            <div style={messageStyle}>{current.opts.message}</div>

            {current.kind === "prompt" &&
              (promptOpts?.multiline ? (
                <textarea
                  ref={inputRef}
                  rows={3}
                  value={value}
                  aria-label={promptOpts?.placeholder ?? t("dialog.confirm", "Confirm")}
                  placeholder={promptOpts?.placeholder}
                  onChange={(e) => { valueRef.current = e.target.value; setValue(e.target.value); }}
                  style={fieldStyle}
                />
              ) : (
                <input
                  ref={inputRef}
                  value={value}
                  aria-label={promptOpts?.placeholder ?? t("dialog.confirm", "Confirm")}
                  placeholder={promptOpts?.placeholder}
                  onChange={(e) => { valueRef.current = e.target.value; setValue(e.target.value); }}
                  style={fieldStyle}
                />
              ))}

            <div style={actions}>
              <button type="button" className="btn" onClick={cancel}>
                {current.opts.cancelLabel ?? t("dialog.cancel", "Cancel")}
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className={current.opts.danger ? "btn" : "btn primary"}
                style={current.opts.danger ? dangerBtn : undefined}
                disabled={!canConfirm}
                onClick={accept}
              >
                {current.opts.confirmLabel ?? t("dialog.confirm", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useConfirm/usePrompt must be used inside <ConfirmProvider>");
  return ctx;
}

/** Returns an async confirm(opts|message) => Promise<boolean>. */
export function useConfirm() {
  return useDialog().confirm;
}

/** Returns an async prompt(opts|message) => Promise<string|null> (null = cancelled). */
export function usePrompt() {
  return useDialog().prompt;
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(11, 58, 111, 0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 16,
};
const panel: CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: 22,
  width: 440,
  maxWidth: "100%",
  boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
};
const titleStyle: CSSProperties = { margin: "0 0 10px", fontSize: 17, color: "#0b3a6f" };
const messageStyle: CSSProperties = { fontSize: 14, color: "var(--ink-2, #374151)", lineHeight: 1.5, whiteSpace: "pre-wrap" };
const fieldStyle: CSSProperties = {
  width: "100%",
  marginTop: 14,
  padding: "9px 12px",
  border: "1px solid var(--border, #e5e7eb)",
  borderRadius: 8,
  fontFamily: "inherit",
  fontSize: 14,
  resize: "vertical",
};
const actions: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 };
const dangerBtn: CSSProperties = { background: "#b91c1c", borderColor: "#b91c1c", color: "#fff" };
